import { FileLocation } from "appworks/config/asset-config-schema";
import { Layers } from "appworks/graphics/layers/layers";
import { LoaderService } from "appworks/loader/loader-service";
import { Services } from "appworks/services/services";
import { SoundEvent } from "appworks/services/sound/sound-events";
import { SoundService } from "appworks/services/sound/sound-service";
import { Contract } from "appworks/utils/contracts/contract";
import { Sequence } from "appworks/utils/contracts/sequence";
import { Timer } from "appworks/utils/timer";
import { LoaderResource } from "pixi.js";
import { Signal } from "signals";
import { JurisdictionService } from "slotworks/services/jurisdiction/jurisdiction-service";
import { AbstractComponent } from "../abstract-component";

enum FillStyle {
    Color,
    LinearGradient,
    RadialGradient
}
enum Phases {
    DelayStart,
    FadeIn,
    ShowProgress1,
    ShowProgress2,
    ShowProgress3,
    ShowProgress4,
    ShowProgress5,
    ShowProgress6,
    FinishProgress,
    FadeOut,
    Cleanup
}
interface Phase {
    name: Phases;
    duration: number;
    untilPercent: number;
}

export class SimplePreloaderComponent extends AbstractComponent {

    // Signals
    public onProgress: Signal = new Signal();
    public onComplete: Signal = new Signal();

    // HTML Element References
    private canvas: HTMLCanvasElement;
    private context: CanvasRenderingContext2D;
    private shine: HTMLImageElement;
    private playLoadingIcons: HTMLElement[];
    private preloaderContainer: HTMLElement;

    // Update Loop
    private progressThread: number;

    // Progress ring style
    private centerX: number;
    private centerY: number;
    private radius: number;
    private lineWidth: number;

    // Progress data
    private actualPercentage: number = 0;
    private shownPercentage: number;

    // Loading phases
    private lastFrameTime: number;
    private movingAverageFPS: number = 1;
    private activePhaseIndex: number;
    private phaseStartFrame: number = 0;
    private phaseStartTime: number;
    private phases: Phase[];
    private phaseChanged: boolean = false;

    // Progress tracking and display
    private files: Map<string, FileLocation> = new Map<string, FileLocation>();
    private loadedFiles: string[] = [];
    private totalKb: number = 0;
    private completeKb: number = 0;
    private frameCount: number = 0;
    private lastAdjustFrame: number = 0;
    private lastAdjustPercentage: number = 0;
    private targetPercentage: number = 0;
    private framesToTarget: number = 60;

    // Update dispatch event
    private lastProgressDispatchPercent: number = 0;
    private progressDispatchComplete: boolean = false;

    public start(files: FileLocation[]) {
        for (const file of files) {
            let fileName = file.name;
            // When loading atlases only care about the image, not the json
            if (file.url.indexOf("@")) {
                fileName += "_image";
            }
            this.files.set(fileName, file);
            this.totalKb += file.size;
        }

        this.shine = document.getElementById("preload-shine") as HTMLImageElement;
        this.canvas = document.getElementById("loader-canvas") as HTMLCanvasElement;
        this.canvas.style.opacity = "0";
        this.canvas.width = Math.max(this.canvas.width, this.canvas.height);
        this.canvas.height = Math.max(this.canvas.width, this.canvas.height);
        this.context = this.canvas.getContext("2d");
        this.centerX = this.canvas.width / 2;
        this.centerY = this.canvas.height / 2;
        this.radius = 120;
        this.lineWidth = 9;
        this.shownPercentage = 0;
        this.activePhaseIndex = 0;
        this.phases = [
            { name: Phases.DelayStart,      untilPercent: null,  duration: null },
            { name: Phases.FadeIn,          untilPercent: null,  duration: 30 },
            { name: Phases.ShowProgress1,   untilPercent: 25,    duration: null },
            { name: Phases.ShowProgress2,   untilPercent: 50,    duration: null },
            { name: Phases.ShowProgress3,   untilPercent: 75,    duration: null },
            { name: Phases.ShowProgress4,   untilPercent: 90,    duration: null },
            { name: Phases.ShowProgress5,   untilPercent: 95,    duration: null },
            { name: Phases.ShowProgress6,   untilPercent: 100,   duration: null },
            { name: Phases.FadeOut,         untilPercent: null,  duration: 30 },
            { name: Phases.Cleanup,         untilPercent: null,  duration: null }
        ];

        this.playLoadingIcons = [
            document.getElementById("preload-play-1"),
            document.getElementById("preload-play-2"),
            document.getElementById("preload-play-3")
        ];
        this.preloaderContainer = document.getElementById("preload-spinner");

        if (Services.get(JurisdictionService).getMinPreloaderTime() > 0) {
            Services.get(LoaderService).minPreloaderTime = Services.get(JurisdictionService).getMinPreloaderTime();
        }

        this.lastFrameTime = Date.now();
        this.progressThread = Timer.setInterval(() => this.updateLoop(), 0);
    }

    public fileLoaded(resource: LoaderResource) {
        this.loadedFiles.push(resource.name);
        if (this.files.has(resource.name)) {
            const fileData = this.files.get(resource.name);
            this.completeKb += fileData.size;
        }
    }

    /**
     * Returns a Contract for when it's done
     */
    public loadComplete() {
        Services.get(SoundService).event(SoundEvent.preload_complete);
        return new Sequence([
            () => Contract.wrap(() => {
                this.onComplete.dispatch();
            })
        ]);
    }

    public destroy() {
        Layers.get("Menus")?.clear();
        Layers.get("Background").show();

        this.hidePreloaderElements();
    }

    public getActualPercentage() {
        const accuracy = 100000000;
        return Math.round(this.completeKb / this.totalKb * accuracy) / accuracy;
    }

    public setProgressComplete() {
        this.actualPercentage = 100;
        this.progressDispatchComplete = true;
        gameWrapper.loadProgressUpdate(100);
    }

    protected updateCheck() {
        const newPercentage = Math.floor(this.actualPercentage);
        const progressChanged = newPercentage - this.lastProgressDispatchPercent >= 1;
        if (!this.progressDispatchComplete && progressChanged) {
            this.lastProgressDispatchPercent = newPercentage;
            this.progressDispatchComplete = (newPercentage >= 100);
            this.update();
        }
    }

    protected update() {
        this.onProgress.dispatch(this.getActualPercentage);
    }

    private updateLoop() {
        this.frameCount++;
        const timeSinceLastFrame = Date.now() - this.lastFrameTime;
        this.lastFrameTime = Date.now();
        this.actualPercentage = this.getActualPercentage() * 100;
        this.updateCheck();
        const activePhase = this.phases[this.activePhaseIndex];
        const phaseFrameDuration = this.frameCount - this.phaseStartFrame;

        switch (activePhase.name) {
            case Phases.DelayStart:
                // Don't start the fade in animation until we've detected that we have a stable framerate.
                const fpsTarget = 30;
                const averageDecay = .99;
                this.movingAverageFPS = timeSinceLastFrame > 1000 ? 1 : averageDecay * this.movingAverageFPS + (1.0 - averageDecay) * (1000 / timeSinceLastFrame);
                if ((this.movingAverageFPS > fpsTarget && this.frameCount > fpsTarget * 4) || (this.actualPercentage >= 100 && phaseFrameDuration > 5 * fpsTarget)) {
                    for (const icon of this.playLoadingIcons) {
                        icon.style.animation = "none";
                        icon.style.opacity = "0";
                    }
                    this.goToNextPhase();
                }
                break;
            case Phases.FadeIn:
                const fadeInT = Math.min(1, phaseFrameDuration / activePhase.duration);
                this.canvas.style.opacity = fadeInT.toString();
                const maxWidth = Math.max(0, 40 * (1 - fadeInT));
                for (const icon of this.playLoadingIcons) {
                    icon.style.maxWidth = maxWidth.toString() + "px";
                }
                this.shownPercentage = 0;
                break;
            case Phases.ShowProgress1:
            case Phases.ShowProgress2:
            case Phases.ShowProgress3:
            case Phases.ShowProgress4:
            case Phases.ShowProgress5:
            case Phases.ShowProgress6:
                if (this.actualPercentage >= 100 && this.shownPercentage < 100 && activePhase.name !== Phases.ShowProgress6) {
                    this.goToNextPhase(Phases.ShowProgress6);
                } else if (this.shownPercentage >= activePhase.untilPercent) {
                    this.goToNextPhase();
                } else {
                    const maxFakePercent = Math.max(95, this.actualPercentage);
                    const framesPassed = this.frameCount - this.lastAdjustFrame;

                    if (this.shownPercentage < maxFakePercent) {
                        const progressT = Math.min(1, framesPassed / this.framesToTarget);
                        const percentDelta = this.targetPercentage - this.lastAdjustPercentage;
                        const newShownPercentage = Math.min(maxFakePercent, (this.targetPercentage - percentDelta) + percentDelta * progressT);
                        let showPercentageDelta = newShownPercentage - this.shownPercentage;
                        if (this.actualPercentage < this.shownPercentage) {
                            const aheadMultiplier = Math.pow(1 - ((this.shownPercentage - this.actualPercentage) / (100 - this.actualPercentage)), 5);
                            showPercentageDelta *= aheadMultiplier;
                        }
                        this.shownPercentage += showPercentageDelta;

                        if (progressT === 1 && this.targetPercentage - this.shownPercentage < .001) { // Account for rounding errors with very small percentages
                            this.shownPercentage = this.targetPercentage;
                        }
                    }

                    if (this.phaseChanged && this.targetPercentage < activePhase.untilPercent) {
                        this.phaseChanged = false;
                        this.adjustSpeed(activePhase);
                    }
                }
                break;
            case Phases.FadeOut:
                const fadeOutT = Math.min(1, phaseFrameDuration / activePhase.duration);
                const opacity = (1 - fadeOutT).toString();
                this.canvas.style.opacity = opacity;
                this.preloaderContainer.style.opacity = opacity;
                break;
            case Phases.Cleanup:
                this.destroy();
                break;
        }

        if (activePhase.duration != null && phaseFrameDuration > activePhase.duration) {
            this.goToNextPhase();
        }

        this.drawBackground();
        this.drawRingProgress(this.shownPercentage, FillStyle.LinearGradient, "#ff5803", "#ffde00");
        if (this.shownPercentage < 100) {
            this.drawRingShine(this.shownPercentage);
        }
    }

    private goToNextPhase(phase?: Phases | null) {
        if (phase == null) {
            this.activePhaseIndex = this.activePhaseIndex + 1;
        } else {
            this.activePhaseIndex = this.phases.findIndex((item) => item.name === phase);
        }
        this.phaseStartTime = Date.now();
        this.phaseStartFrame = this.frameCount;
        this.phaseChanged = true;
    }

    // Update targetPercentage and framesToTarget to set to progress ring speed.
    private adjustSpeed(activePhase: Phase) {
        let nextPercentTarget = Math.max(this.targetPercentage, activePhase.untilPercent);
        if (this.actualPercentage > this.shownPercentage) {
            nextPercentTarget = Math.ceil(this.actualPercentage / 25) * 25;
        }

        this.targetPercentage = nextPercentTarget;
        this.lastAdjustFrame = this.frameCount;
        this.lastAdjustPercentage = this.shownPercentage;

        if (this.actualPercentage >= 100) {
            this.framesToTarget = this.targetPercentage - this.shownPercentage;
        } else {
            const speedMultiplier = (this.actualPercentage + 5) / (this.shownPercentage + 5);
            const baseFramesToTarget = 60 * 20 / speedMultiplier; // 20 seconds
            const maxPercentPerFrame = 1;
            const minFrames = (this.targetPercentage - this.shownPercentage) / maxPercentPerFrame;
            const maxFramesToPercent = 50;
            const maxFrames = (this.targetPercentage - this.shownPercentage) * maxFramesToPercent;
            this.framesToTarget = Math.min(Math.max(baseFramesToTarget / speedMultiplier, minFrames), maxFrames);
        }
    }

    private drawBackground() {
        this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
        this.drawRingProgress(
            100,
            FillStyle.RadialGradient,
            "rgba(30, 30, 30, .7)",
            "rgba(0, 9, 23, .7)"
        );
    }

    private drawRingProgress(newPercentage: number, fillStyle: FillStyle, color1: string, color2?: string, clockwise = true) {
        const angle = newPercentage / 100 * 360;

        this.context.beginPath();
        let strokeStyle: string | CanvasGradient = "magenta";
        switch (fillStyle) {
            case FillStyle.Color:
                strokeStyle = color1;
                break;
            case FillStyle.LinearGradient:
                strokeStyle = this.context.createLinearGradient(
                    this.centerX, this.centerY - this.radius,
                    this.centerX, this.centerY + this.radius
                );
                strokeStyle.addColorStop(0, color1);
                strokeStyle.addColorStop(1, color2);
                break;
            case FillStyle.RadialGradient:
                strokeStyle = this.context.createRadialGradient(
                    this.centerX, this.centerY, this.radius - this.lineWidth,
                    this.centerX, this.centerY, this.radius
                );
                strokeStyle.addColorStop((this.radius - this.lineWidth) / this.radius, color1);
                strokeStyle.addColorStop(1, color2);
                break;
        }
        this.context.strokeStyle = strokeStyle;
        this.context.arc(
            this.centerX,
            this.centerY,
            this.radius,
            -90 * Math.PI / 180,
            ((-90 + (clockwise ? 1 : -1) * angle) * Math.PI / 180),
            !clockwise
        );
        this.context.lineWidth = this.lineWidth;
        this.context.stroke();
        this.context.closePath();
    }

    private drawRingShine(newPercentage: number, clockwise = true) {
        newPercentage = (clockwise ? -1 : 1) * newPercentage;
        const angle = newPercentage / 100 * 360;
        const offset = this.shine.width / 2;

        this.context.translate(this.centerX, this.centerY);
        this.context.rotate(Math.PI / 180 * -angle);
        this.context.drawImage(this.shine, -offset, -this.radius - offset);
        this.context.rotate(Math.PI / 180 * angle);
        this.context.translate(-this.centerX, -this.centerY);
    }

    private hidePreloaderElements() {
        this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
        Timer.clearInterval(this.progressThread);
        this.canvas.remove();
        if (document.getElementById("preload-spinner")) {
            document.getElementById("preload-spinner").style.display = "none";
        }
    }
}
